
/**
 * Module dependencies.
 */

import { CommercialContactProps, ContactsPageProps } from 'src/types/contacts';
import { ContactLink, Link } from 'src/components/core/contacts/links';
import { assetUrlResolve } from 'src/core/utils/url-resolver';
import { color, media, units } from 'src/styles/utils';
import { convertCountriesToLocale } from 'src/core/utils/countries';
import { getApiPageSlugByLocale } from 'src/core/utils/routes';
import { ifProp } from 'styled-tools';
import { useTranslate } from 'src/core/utils/translator';
import Container from 'src/components/core/layout/container';
import FadeInUpAnimation from 'src/components/core/animations/fade-in-up';
import Image, { ImageProps } from 'src/components/core/image';
import Loading from 'src/components/core/loading';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import Select, { ItemOption } from 'src/components/core/select';
import Type from 'src/components/core/typography';
import filter from 'lodash/filter';
import find from 'lodash/find';
import flatten from 'lodash/flatten';
import head from 'lodash/head';
import isEmpty from 'lodash/isEmpty';
import lowerCase from 'lodash/lowerCase';
import map from 'lodash/map';
import orderBy from 'lodash/orderBy';
import styled, { css } from 'styled-components';
import uniq from 'lodash/uniq';
import useCommercialContacts from 'src/api/app/contacts/use-commercial-contacts';
import usePage from 'src/api/app/pages/use-page';

/**
 * `Props` type.
 */

type Props = {
  isKiosk?: boolean
};

/**
 * `Section` styled component.
 */

const Section = styled.section<{ isLoading: boolean }>`
  background-color: ${color('beige200')};
  overflow: hidden;
  padding-bottom: ${units(13)};
  position: relative;

  ${media.min('ms')`
    padding-bottom: ${units(17)};
  `}

  ${ifProp('isLoading', css`
    min-height: 500px;
  `)}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-template-areas:
    'title'
    'desc'
    'commercial';
  grid-template-columns: 100%;
  grid-template-rows: repeat(3, max-content);

  ${media.min('ms')`
    grid-template-areas:
      '. title title commercial .'
      '. desc  . commercial .';
    grid-template-columns: 1fr minmax(235px, 2fr) 1fr minmax(328px, 5fr) 1fr;
    grid-template-rows: max-content 1fr;
  `}

  ${media.min('md')`
    grid-template-areas:
      '. title title . commercial .'
      '. desc  .     . commercial .';
    grid-template-columns: 1fr 3fr 2fr 1fr minmax(328px, 4fr) 1fr;
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H2)`
  grid-area: title;
  margin-bottom: ${units(6)};

  ${media.min('ms')`
    margin: 0;
  `}
`;

/**
 * `DescriptionWrapper` styled component.
 */

const DescriptionWrapper = styled.div`
  grid-area: desc;
`;

/**
 * `Description` styled component.
 */

const Description = styled(Type.H6).attrs({ as: 'p', small: true })`
  color: ${color('grey400')};
`;

/**
 * `CommercialContactsWrapper` styled component.
 */

const CommercialContactsWrapper = styled.div`
  grid-area: commercial;
`;

/**
 * `SelectLabel` styled component.
 */

const SelectLabel = styled(Type.H6).attrs({ as: 'p' })`
  grid-area: country;
  margin-bottom: ${units(1.5)};
`;

/**
 * `CountrySelect` styled component.
 */

const CountrySelect = styled(Select)`
  grid-area: select;
  margin-bottom: ${units(6)};
`;

/**
 * `CountryName` styled component.
 */

const CountryName = styled(Type.H6).attrs({ as: 'p' })`
  margin-bottom: ${units(3)};

  ${media.min('ms')`
    margin-bottom: ${units(6)};
  `}
`;

/**
 * `Contacts` styled component.
 */

const Contacts = styled.div`
  color: ${color('grey400')};
  grid-area: contacts;
`;

/**
 * `CommercialContactWrapper` styled component.
 */

const CommercialContactWrapper = styled.div`
  display: grid;
  grid-gap: ${units(2)};
  grid-template-columns: ${units(12.5)} 1fr;
  margin-bottom: ${units(3)};

  ${media.min('lg')`
    grid-template-columns: ${units(16)} 1fr;
  `}
`;

/**
 * `ContactPhotoWrapper` styled component.
 */

const ContactPhotoWrapper = styled.div`
  height: ${units(12.5)};
  position: relative;

  ${media.min('lg')`
    height: ${units(16)};
  `}
`;

/**
 * `StyledImage` styled component.
 */

const StyledImage = styled(Image)<ImageProps>`
  border-radius: 50%;
  transition: transform 2s ease-in;
`;

/**
 * `CommercialContact` styled component.
 */

const CommercialContactInfo = styled(Type.H6).attrs({
  as: 'p',
  small: true
})`
  color: ${color('grey400')};
`;

/**
 * `CommercialContacts` component.
 */

const CommercialContacts = ({ isKiosk }: Props): ReactElement => {
  const { locale, translate } = useTranslate();
  const {
    data: pageData,
    isLoading: isLoadingPage,
    isSuccess: isSuccessPage
  } = usePage<ContactsPageProps>({
    slug: getApiPageSlugByLocale('contacts', locale)
  });

  const {
    data: commercialContacts,
    isLoading: isLoadingCommercialContacts,
    isSuccess: isSuccessCommercialContacts
  } = useCommercialContacts();

  const isLoading = isLoadingPage || isLoadingCommercialContacts;
  const isSuccess = isSuccessPage && isSuccessCommercialContacts;
  const { defaultOption, options } = useMemo(() => {
    const alpha2Codes: string[] = uniq(flatten(map(filter(commercialContacts, ({ countries }) => !isEmpty(countries)), ({ countries }) => {
      return map(countries, ({ alpha2 }) => alpha2);
    })));

    const convertedAlpha2Codes = convertCountriesToLocale(alpha2Codes, locale);
    const options = map(convertedAlpha2Codes, ({ alpha2, name }) => ({
      label: name,
      value: alpha2
    }));

    const orderedOptions = orderBy(options, [({ label }) => lowerCase(label)], ['asc']);
    const hasPortugal = find(orderedOptions, ['value', 'PT']);
    const defaultOption: ItemOption = hasPortugal ?? head(orderedOptions);

    return {
      defaultOption,
      options: orderedOptions
    };
  }, [commercialContacts, locale]);

  const [selectedCountry, setSelectedCountry] = useState<ItemOption>();
  const commercialContactsByCountry = useMemo(() => {
    return filter(commercialContacts, ({ countries }) => {
      return find(countries, ['alpha2', selectedCountry?.value]);
    });
  }, [commercialContacts, selectedCountry]);

  useEffect(() => {
    if (!selectedCountry) {
      setSelectedCountry(defaultOption);
    }
  }, [defaultOption, selectedCountry]);

  return (
    <Section isLoading={isLoading}>
      <Loading active={isLoading} />

      {isSuccess && (
        <Container>
          <Grid>
            {!isEmpty(options) && (
              <>
                <Title>
                  <FadeInUpAnimation>
                    {pageData?.secondaryTitle}
                  </FadeInUpAnimation>
                </Title>

                <DescriptionWrapper>
                  <FadeInUpAnimation>
                    <Description>
                      {pageData?.secondaryDescription}
                    </Description>
                  </FadeInUpAnimation>
                </DescriptionWrapper>

                <CommercialContactsWrapper>
                  <SelectLabel id={'countries_label'}>
                    {translate('common:sections.contacts.country')}
                  </SelectLabel>

                  <CountrySelect
                    defaultValue={defaultOption}
                    disabled={isLoading}
                    name={'countries'}
                    onChange={setSelectedCountry}
                    options={options}
                    placeholder={translate('forms:selectPlaceholder')}
                    value={selectedCountry}
                  />

                  <Contacts>
                    {selectedCountry && isEmpty(commercialContactsByCountry) ? (
                      <Type.Paragraph>
                        {`${translate('common:sections.contacts.noCommercialContacts')} ${selectedCountry?.label}`}
                      </Type.Paragraph>
                    ) : (
                      <>
                        <CountryName>
                          {selectedCountry?.label}
                        </CountryName>

                        {map(commercialContactsByCountry, (ccProps: CommercialContactProps) => {
                          const {
                            email,
                            fax,
                            id,
                            media,
                            name,
                            phone,
                            position
                          } = ccProps;

                          return (
                            <CommercialContactWrapper key={id}>
                              <ContactPhotoWrapper>
                                <StyledImage
                                  alt={name}
                                  layout={'fill'}
                                  objectFit={'cover'}
                                  src={media[0]?.fileUrl ?? assetUrlResolve('/static/images/empty/list@2x.jpg')}
                                />
                              </ContactPhotoWrapper>

                              <CommercialContactInfo>
                                {name}
                                
                                {position && <><br />{position.name}</>}

                                {email && (isKiosk ? (
                                  <><br />{email}</>
                                ) : (
                                  <div>
                                    <ContactLink
                                      href={`mailto:${email}`}
                                      rel={'noopener'}
                                      target={'_blank'}
                                    >
                                      {email}
                                    </ContactLink>
                                  </div>
                                ))}

                                {phone && (isKiosk ? (
                                  <><br />{phone}</>
                                ) : (
                                  <div>
                                    <Link i18nkey={translate('common:sections.contacts.mobile', { phone })} />
                                  </div>
                                ))}

                                {fax && (isKiosk ? (
                                  <><br />{fax}</>
                                ) : (
                                  <div>
                                    <Link i18nkey={translate('common:sections.contacts.fax', { fax })} />
                                  </div>
                                ))}
                              </CommercialContactInfo>
                            </CommercialContactWrapper>
                          );
                        })}
                      </>
                    )}
                  </Contacts>
                </CommercialContactsWrapper>
              </>
            )}
          </Grid>
        </Container>
      )}
    </Section>
  );
};

/**
 * Export `CommercialContacts` component.
 */

export default CommercialContacts;
